// Map Review column registry. Keys are shared verbatim with the server's
// registry (platform/map_review/spec.go); `prio` is the responsive priority — 1
// is the widest column and is kept longest, higher numbers are dropped first
// when the container narrows. The first column of every table is the row's
// identity — its label, its name, its moment — and is rendered sticky.
//
// No ID is filterable anywhere. Where a record carries no label of its own, the
// row's identity is its id: a Trap Location has none, and the lab's Pool is
// recorded before it is labelled, so its label is nullable. A Feature is
// identified by its generated id because most Features carry no description,
// whereas a Site is identified by its label and a Treatment by its date.
//
// Enum option lists for kind / owner_kind / method / has_features / feature.type
// are static here; `treatment.product`, `treatment.intervention` and
// `treatment.creator` options come from a store at render time (the product,
// intervention and user stores).
import { featureTypeLabel } from "@/lib/featureType";
import type {
	MapReviewColumnFilter,
	MapReviewColumnSummary,
	MapReviewFilterOp,
	MapReviewSort,
	MapReviewSpec,
	MapReviewTable,
} from "@/type/api";

export type MapReviewColumnKind =
	| "text"
	| "number"
	| "bool"
	| "datetime"
	// A bare calendar day with no meaningful clock time (a revisit date, a
	// stored day): rendered as an absolute date, never a relative phrase.
	| "date"
	| "enum"
	// A treated area (stored in canonical square metres): rendered in the
	// district's resolved area unit, with the unit named in the header. The
	// server supplies the converted value and unit; the client never converts.
	| "area"
	// The product name as a link to its catalog detail page; the row carries
	// the catalog id.
	| "productLink"
	// A treatment's prescribed label rate scenarios, grouped by basis.
	| "rates"
	// The treatment's own applied rate plus its over/under-label verdict
	// (display-only; the server derives the rate, the client never computes it).
	| "appliedRate"
	// A Trap Sample's catch as one summary cell — total and female total, or the
	// reason there is no reading ("Not collected", "Not analyzed"). Display-only;
	// the catch is filtered through the counts clause, never by the cell's value.
	| "counts"
	// A Site's tags as one chip per tag (doc/in-progress/
	// site-tagging-and-symbology.md §8.2): the chips are drawn from the row's
	// `tag_ids` and the symbology catalog's tag list, and the cell falls back to
	// the column's plain text — the comma-joined names — when the catalog has
	// not loaded or an id it names is not in it.
	| "tags";

export interface MapReviewColumnOption {
	value: string | number;
	label: string;
}

export interface MapReviewColumn {
	key: string;
	label: string;
	kind: MapReviewColumnKind;
	prio: number;
	sortable: boolean;
	filterOps: MapReviewFilterOp[];
	/** Layout hint in CSS pixels at the default font size. */
	width: number;
	align?: "left" | "right";
	/** Enum option list, static in this registry. */
	options?: MapReviewColumnOption[];
	/** Value→label map for the CELL. An enum cell renders the option's label
	 * rather than the wire value (an observation's inspection reads "Found
	 * none", not "none"), so the reading an operator filters by is the reading
	 * they see. Derived from `options`, so cell and filter cannot drift. */
	valueLabels?: Record<string, string>;
	/** Enum whose option list comes from a store: the product store's products,
	 * the intervention store's interventions, the user store's organisation
	 * members, all by id, or one of the district's live vocabularies. A
	 * vocabulary naming an id-backed enum (a species, a trap type, a lure, a test
	 * target, a test method) offers the rows' ids; the two whose property is a
	 * FACET (sex, and the test status) offer the facet, because that is what the
	 * server's clause matches. */
	optionsSource?:
		| "product"
		| "intervention"
		| "user"
		| "species"
		| "sex"
		| "trapType"
		| "lure"
		| "testTarget"
		| "testMethod"
		| "testStatus";
	/** Enum values are numbers (product, intervention or user ids) rather than
	 * strings. */
	optionValueKind?: "string" | "number";
	/**
	 * The domain a numeric column's slider spans, in the column's canonical
	 * unit. Magnitude-spaced (`log`) for both of these because their values run
	 * over orders of magnitude — quantity median 1, p95 32, max 21,575; area
	 * median 1.9 m², p95 4,047 m², max 30.7M m². The ends are set to cover the
	 * whole observed range rather than a tidy bound, so dragging can reach any
	 * value in the data; the server counts the buckets for this domain, so this
	 * is the one place the scale is defined.
	 */
	rangeDomain?: { min: number; max: number; log: boolean };
	/**
	 * When the cell's own value is empty, the row key to fall back to, with an
	 * optional prefix. The Observations ledger's Technician column falls back
	 * to the source's zone, which names no person but is what the district
	 * routed the visit by; a named technician always wins.
	 */
	valueFallback?: { key: string; prefix?: string };
	/**
	 * When set, the cell's value is a button linking to the related table:
	 * clicking switches the active table to `target` and narrows it to the
	 * records related to this row. A singular relation shows the related
	 * record's display value; a plural relation shows its count.
	 */
	relation?: { target: MapReviewTable };
	/** The cell narrows `target` to this row's zone: clicking switches to that
	 *  table with `spec.zones = [this row's id]`. It is deliberately not a
	 *  relation clause — a zone relates to no record by stored reference, the
	 *  server has no zone relation, and the narrowing is geometric. */
	zoneLink?: { target: MapReviewTable };
}

/** The column key a relation filter carries (a table has at most one). */
export const RELATION_COLUMN = "relation";

/** The ledgers' virtual cross-column `search` property (`resource/ledger.go`):
 * the one text field that matches a term across a record's whole searchable
 * content — its own fields and, where it has one, its Site's label and address.
 * It exists for the Search pane's `contains` and is never a ledger filter
 * clause: `ledgerFilter` only translates `spec.filters`, whose columns are the
 * registry's, and `search` declares no column, cell, sort or footer. */
export const SEARCH_PROPERTY = "search";

/** Okabe–Ito colourblind-safe pair for the rate verdict (distinguishable under
 * protanopia, deuteranopia and tritanopia). Colour is never the only cue: the
 * applied-rate cell carries a text cue and the row a left rule in the same
 * colour. Imported by DataTable.vue and MapReview.vue so the table and the map
 * cannot drift. */
export const RATE_OVER_COLOR = "#D55E00"; // vermillion
export const RATE_UNDER_COLOR = "#0072B2"; // blue

// The Trap Sample's stored analysis state (§25 Rule 3): the sample exists and
// its composition is not known yet, or it is. The worklist is the collected
// samples narrowed to the first, which is why there is no reconciled flag.
const SAMPLE_STATE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "not_analyzed", label: "Not analyzed" },
	{ value: "analyzed", label: "Analyzed" },
];
export const SAMPLE_STATE_LABELS: Record<string, string> = {
	not_analyzed: "Not analyzed",
	analyzed: "Analyzed",
};

// The tables the ledger can show, in tab order. The trap family's four surfaces
// sit after the four ledger tables: Trap Locations, the Trap Instances set at
// them, the Mosquito Counts those instances produced, and the Trap Samples the
// lab reconciles the box against. The district's own Zones sit last, after the
// trap family: they are not records about the world but the units every ledger
// can be narrowed by.
export const MAP_REVIEW_TABLES: MapReviewTable[] = [
	"site",
	"feature",
	"treatment",
	"observation",
	"trap_station",
	"trap_instance",
	"species_count",
	"trap_sample",
	"zone",
];

export const MAP_REVIEW_TABLE_LABELS: Record<MapReviewTable, string> = {
	site: "Sites",
	feature: "Features",
	treatment: "Treatments",
	observation: "Observations",
	trap_station: "Trap Locations",
	trap_instance: "Trap Instances",
	species_count: "Mosquito Counts",
	trap_sample: "Trap Samples",
	zone: "Zones",
	pool: "Pools",
};

/** Singular display name of a table, for relation-filter labels. */
export const MAP_REVIEW_TABLE_SINGULAR: Record<MapReviewTable, string> = {
	site: "Site",
	feature: "Feature",
	treatment: "Treatment",
	observation: "Observation",
	trap_station: "Trap Location",
	trap_instance: "Trap Instance",
	species_count: "Mosquito count",
	trap_sample: "Trap sample",
	zone: "Zone",
	pool: "Pool",
};

// Static enum option lists. site_kind and treatment_method were read from the
// live database enum types (parcel|street|other, chemical|biological|physical);
// site_owner_kind likewise (private|city|county|state|federal|district|
// unknown).
const SITE_KIND_OPTIONS: MapReviewColumnOption[] = [
	{ value: "parcel", label: "Parcel" },
	{ value: "street", label: "Street" },
	{ value: "other", label: "Other" },
];

const SITE_OWNER_KIND_OPTIONS: MapReviewColumnOption[] = [
	{ value: "private", label: "Private" },
	{ value: "city", label: "City" },
	{ value: "county", label: "County" },
	{ value: "state", label: "State" },
	{ value: "federal", label: "Federal" },
	{ value: "district", label: "District" },
	{ value: "unknown", label: "Unknown" },
];

const TREATMENT_METHOD_OPTIONS: MapReviewColumnOption[] = [
	{ value: "chemical", label: "Chemical" },
	{ value: "biological", label: "Biological" },
	{ value: "physical", label: "Physical" },
];

// The Has features column is a boolean but filters through the enum op; the
// wire values stay booleans-as-strings so the server can read them with
// ParseBool.
const YES_NO_OPTIONS: MapReviewColumnOption[] = [
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

// The Applied rate column's verdict vocabulary, shared with the server's
// registry (platform/map_review/spec.go): the four values the derivation reports.
// Filtering by it narrows the table — and the map — to exactly the treatments
// with those verdicts.
const RATE_VERDICT_OPTIONS: MapReviewColumnOption[] = [
	{ value: "over", label: "Over label" },
	{ value: "under", label: "Under label" },
	{ value: "in_range", label: "Within label" },
	{ value: "unknown", label: "No basis" },
	{ value: "not_applicable", label: "Not applicable" },
];

// The Mosquito counts row's three-valued positivity reading (§28 System Rule
// 1): at least one test positive, the pools tested and none positive, or no
// test at all — which is not the same as testing negative.
const POSITIVITY_OPTIONS: MapReviewColumnOption[] = [
	{ value: "positive", label: "Positive" },
	{ value: "negative", label: "Negative" },
	{ value: "not_tested", label: "Not tested" },
];
export const POSITIVITY_LABELS: Record<string, string> = {
	positive: "Positive",
	negative: "Negative",
	not_tested: "Not tested",
};

// The Trap Instance's derived state (§24): Set while the trap is out, Collected
// once it has been picked up.
const INSTANCE_STATE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "set", label: "Set" },
	{ value: "collected", label: "Collected" },
];
export const INSTANCE_STATE_LABELS: Record<string, string> = {
	set: "Set",
	collected: "Collected",
};

/** The counts filter's sex facets, in the vocabulary's order: what the counts
 * clause's sex component offers. A facet rather than a vocabulary id, so
 * "female" means every female state the source distinguishes. */
export const SEX_FACET_OPTIONS: MapReviewColumnOption[] = [
	{ value: "female", label: "Female" },
	{ value: "male", label: "Male" },
	{ value: "unknown", label: "Unknown sex" },
];

// What one recorded test came back as — the `test_status` vocabulary's four
// facets, which the pool read's Status column filters by and its cell reads in
// the operator's words. The facet rather than a row id, for the same reason the
// sex facets are: the clause matches `test_status.facet`, so "inconclusive"
// reaches every state the district spells that way. "Pending" is a result
// state and not an absent test — a pool with no test at all reads no status.
const TEST_STATUS_OPTIONS: MapReviewColumnOption[] = [
	{ value: "positive", label: "Positive" },
	{ value: "negative", label: "Negative" },
	{ value: "inconclusive", label: "Inconclusive" },
	{ value: "pending", label: "Pending" },
];
const TEST_STATUS_LABELS: Record<string, string> = {
	positive: "Positive",
	negative: "Negative",
	inconclusive: "Inconclusive",
	pending: "Pending",
};

// The habitat_class enum's declared values, in the generated model's order
// (db/gen/nidus-sync/public/model/habitat_class.go). The Feature `type` column
// filters over the DERIVED type (platform/feature.go featureType): the habitat
// class when the Feature has one, else "pool" for a detected pool the source
// never classified, else "" for an unclassified Feature. The vocabulary is
// therefore the habitat classes plus those two non-habitat values, so a filter
// can reach the pool-only and unclassified Features rather than folding them
// into a habitat class. Labels reuse featureTypeLabel so an option reads
// exactly as the Feature's type does elsewhere.
const HABITAT_CLASS_VALUES: string[] = [
	"swimming_pool",
	"gutter",
	"drain",
	"depression",
	"orchard",
	"pond",
	"fountain_or_water_feature",
	"yard_drain",
	"ditch",
	"dairy_lagoon",
	"misc_water_accumulation",
	"trough",
	"pasture",
	"plant_saucer",
	"utility",
	"row_crops",
	"wastewater_treatment",
	"irrigation_standpipe",
	"containers",
	"catch_basin",
	"ag_grasses_or_grain",
	"bird_bath",
	"hot_tub",
	"slough",
	"sump",
	"watering_bowl",
	"vine_crops",
	"aboveground_pool",
	"culvert",
	"stream_or_creek",
	"kid_pool",
	"river",
	"low_point",
	"marsh_or_wetland",
	"appliance",
	"flooded_structure",
	"rain_gutter",
	"treehole",
	"tires",
	"retention_basin",
	"tail_ditch",
	"tarp_or_cover",
	"plant_axil",
	"rice",
	"lake",
	"septic",
	"log_decks",
];

const FEATURE_TYPE_OPTIONS: MapReviewColumnOption[] = [
	...HABITAT_CLASS_VALUES.map((value) => ({
		value,
		label: featureTypeLabel(value),
	})),
	{ value: "pool", label: featureTypeLabel("pool") },
	{ value: "", label: featureTypeLabel("") },
];

// The Feature `type` cell reads the same label the filter offers, so the row
// shows "Swimming Pool" rather than the wire token `swimming_pool` and the
// reading an operator narrows by is the reading they see.
const FEATURE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
	FEATURE_TYPE_OPTIONS.map((option) => [String(option.value), option.label]),
);
// The Observation ledger's as-of property vocabularies, read from the database
// enum types the versioned tables assert. Each list is the dimension's whole
// domain — the server refuses a member outside it — and one map serves both the
// cell and the filter so the reading shown and the reading filtered cannot
// drift.
const PRODUCTION_STATE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "unknown", label: "Unknown" },
	{ value: "dry", label: "Dry" },
	{ value: "wet", label: "Wet" },
	{ value: "inspected_non_productive", label: "Inspected non-productive" },
	{ value: "source", label: "Source" },
	{ value: "removed", label: "Removed" },
];

const WATER_ORIGIN_OPTIONS: MapReviewColumnOption[] = [
	{ value: "stored_water", label: "Stored water" },
	{
		value: "recreational_or_ornamental_water",
		label: "Recreational or ornamental water",
	},
	{ value: "sprinkler_irrigation", label: "Sprinkler irrigation" },
	{ value: "flood_irrigation", label: "Flood irrigation" },
	{ value: "drip_irrigation", label: "Drip irrigation" },
	{ value: "irrigation_runoff", label: "Irrigation runoff" },
	{ value: "leak", label: "Leak" },
	{ value: "rainwater_accumulation", label: "Rainwater accumulation" },
	{
		value: "stormwater_or_municipal_runoff",
		label: "Stormwater or municipal runoff",
	},
	{ value: "water_conveyance", label: "Water conveyance" },
	{ value: "furrow_irrigation", label: "Furrow irrigation" },
	{ value: "industrial_runoff", label: "Industrial runoff" },
	{ value: "temporary_natural_water", label: "Temporary natural water" },
	{ value: "wastewater_system", label: "Wastewater system" },
	{ value: "seepage", label: "Seepage" },
	{ value: "wastewater_irrigation", label: "Wastewater irrigation" },
	{ value: "permanent_natural_water", label: "Permanent natural water" },
];

const FLOW_STATE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "stagnant", label: "Stagnant" },
	{ value: "slow_flowing", label: "Slow flowing" },
	{ value: "fast_flowing", label: "Fast flowing" },
];

const ORGANIC_CONTENT_OPTIONS: MapReviewColumnOption[] = [
	{ value: "clear", label: "Clear" },
	{ value: "light", label: "Light" },
	{ value: "heavy", label: "Heavy" },
];

const VEGETATION_INFLUENCE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "none", label: "None" },
	{ value: "incidental", label: "Incidental" },
	{ value: "influential", label: "Influential" },
	{ value: "constraining", label: "Constraining" },
];

/** The value→label map a cell reads, derived from the very options its filter
 * offers. */
function optionLabels(
	options: MapReviewColumnOption[],
): Record<string, string> {
	return Object.fromEntries(
		options.map((option) => [String(option.value), option.label]),
	);
}

// The Feature control_state column's vocabulary — the three values the server's
// derived control verdict reports (platform/residual.go ControlState). The
// clause reads the same effective-window derivation the map bands by, so
// filtering to "Past control" reproduces exactly what the past-control map
// toggle did. The labels match the control pane's band rows so the two read as
// one vocabulary.
const CONTROL_STATE_OPTIONS: MapReviewColumnOption[] = [
	{ value: "expired", label: "Past control" },
	{ value: "controlled", label: "Controlled" },
	{ value: "unknown", label: "Control unknown" },
];

// The observation's inspection reading — the three answers the production check
// can give, in the spellings the server reports. "Found none" and "No
// inspection" are deliberately two entries: an Inspection that found nothing
// and a visit that produced no Inspection at all (a dry Feature, a routine
// visit) are different facts, and the ledger must be able to ask for either.
// One label map serves both the cell and the filter, so the reading shown and
// the reading filtered cannot drift.
export const INSPECTION_READING_LABELS: Record<string, string> = {
	found: "Production found",
	none: "Found none",
	no_inspection: "No inspection",
};

// The reading Inspection's own density and stage (the inspection_density and
// inspection_stage vocabularies). Shown on the observation row, never filtered:
// the production reading above is what the ledger narrows by.
export const INSPECTION_DENSITY_LABELS: Record<string, string> = {
	zero: "None",
	trace: "Trace",
	low: "Low",
	medium: "Medium",
	high: "High",
	loaded: "Loaded",
};

export const INSPECTION_STAGE_LABELS: Record<string, string> = {
	early: "Early",
	late: "Late",
	mixed: "Mixed",
};

export const MAP_REVIEW_COLUMNS: Record<MapReviewTable, MapReviewColumn[]> = {
	site: [
		{
			key: "label",
			label: "Site",
			kind: "text",
			prio: 1,
			sortable: true,
			filterOps: ["contains"],
			width: 240,
		},
		{
			key: "kind",
			label: "Kind",
			kind: "enum",
			prio: 2,
			sortable: true,
			filterOps: ["enum"],
			width: 100,
			options: SITE_KIND_OPTIONS,
		},
		{
			key: "address",
			label: "Address",
			kind: "text",
			prio: 3,
			sortable: true,
			filterOps: ["contains"],
			width: 260,
		},
		{
			key: "owner_name",
			label: "Owner",
			kind: "text",
			prio: 4,
			sortable: true,
			filterOps: ["contains"],
			width: 180,
		},
		{
			key: "owner_kind",
			label: "Owner kind",
			kind: "enum",
			prio: 5,
			sortable: true,
			filterOps: ["enum"],
			width: 120,
			options: SITE_OWNER_KIND_OPTIONS,
		},
		{
			key: "has_features",
			label: "Has features",
			kind: "bool",
			prio: 6,
			// Server-backed by EXISTS over feature.site_id — the predicate the
			// correlated `features` count underneath cannot give (ordering by a
			// count scans every candidate site).
			sortable: true,
			filterOps: ["enum"],
			width: 110,
			options: YES_NO_OPTIONS,
		},
		{
			key: "has_traps",
			label: "Has traps",
			kind: "bool",
			prio: 7,
			// Server-backed by EXISTS over trap_station.site_id restricted to a
			// LIVE (non-retired) station — the Site-scale answer to "which sites
			// contain a trap". It is a column, not a relation edge: a trap clause
			// never narrows the Sites ledger and a Site clause never narrows the
			// trap tables. Same shape as has_features, sortable included.
			sortable: true,
			filterOps: ["enum"],
			width: 110,
			options: YES_NO_OPTIONS,
		},
		{
			key: "features",
			label: "Features",
			kind: "number",
			prio: 8,
			// Display-only: the value is a correlated count, so ordering by it
			// renders invalid SQL and filtering by it scans every candidate
			// site. The table still shows the count for each row, and the cell
			// links to the site's Features.
			sortable: false,
			filterOps: [],
			width: 100,
			align: "right",
			relation: { target: "feature" },
		},
		{
			key: "created",
			label: "Created",
			kind: "datetime",
			prio: 9,
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "assigned_to",
			label: "Assigned to",
			kind: "text",
			// Display and filter only: the technician reaches the row through
			// the site's assignment join, so no ORDER BY can drive it without
			// widening the id scan the page already covers. The enum is the
			// org's members by id, exactly as the two creator columns are, so
			// the filter panel offers people and the clause narrows by
			// identity rather than by a name two members could share.
			prio: 10,
			sortable: false,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "user",
			optionValueKind: "number",
		},
		{
			key: "assigned_on",
			label: "Assigned on",
			kind: "date",
			// The day the assignment is for, as a bare calendar day. It arrives
			// through the same join as assigned_to, so it is likewise display
			// and filter only.
			prio: 11,
			sortable: false,
			filterOps: ["dateRange"],
			width: 130,
		},
		{
			key: "notes",
			label: "Notes",
			kind: "text",
			prio: 12,
			sortable: false,
			filterOps: ["contains"],
			width: 220,
		},
		{
			key: "tags",
			label: "Tags",
			// The cell draws a chip per tag from the row's `tag_ids` and the
			// symbology catalog; the clause stays the legacy `tags` text
			// `contains`, which matches the joined names the server still sends.
			kind: "tags",
			prio: 13,
			sortable: false,
			filterOps: ["contains"],
			width: 180,
		},
	],
	feature: [
		{
			key: "id",
			label: "ID",
			kind: "number",
			prio: 1,
			// The identifier, and the only ID the grid shows. Not filterable:
			// no ID is.
			sortable: true,
			filterOps: [],
			width: 90,
			align: "right",
		},
		{
			key: "site",
			label: "Site",
			kind: "text",
			prio: 2,
			sortable: true,
			filterOps: [],
			width: 240,
			relation: { target: "site" },
		},
		{
			key: "type",
			// Habitat is what this column is: the Feature's habitat class, with
			// the pool question folded in (platform.featureType). The KEY stays
			// `type` because the resolver's clause tests the derived type
			// expression under it, and the value vocabulary is renamed nowhere.
			label: "Habitat",
			kind: "enum",
			prio: 3,
			// The type folds in the pool question (platform.featureType), so
			// there is no separate Pool column. It filters over that same derived
			// value: the server tests the type expression, never a stored
			// habitat_class, so the pool-only and unclassified Features are
			// selectable too.
			sortable: false,
			filterOps: ["enum"],
			width: 140,
			options: FEATURE_TYPE_OPTIONS,
			valueLabels: FEATURE_TYPE_LABELS,
		},
		{
			key: "description",
			label: "Description",
			kind: "text",
			prio: 4,
			sortable: false,
			filterOps: ["contains"],
			width: 260,
		},
		{
			key: "created",
			label: "Created",
			kind: "datetime",
			prio: 5,
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "treatments",
			label: "Treatments",
			kind: "number",
			// Display-only for the same reason as site.features; the cell links
			// to the feature's Treatments. Least priority: it is the one column
			// the ledger sheds first when the container is narrow, so the
			// authoritative columns (Control, Active) stay reachable.
			prio: 11,
			sortable: false,
			filterOps: [],
			width: 110,
			align: "right",
			relation: { target: "treatment" },
		},
		{
			key: "calculated_revisit_at",
			label: "Revisit (calculated)",
			kind: "date",
			prio: 7,
			// The cumulative window end the server computes from the Feature's
			// residual expiries. A bare calendar day: the cell renders it as an
			// absolute date (its weekday in the tooltip), never a relative
			// phrase. Sortable (the resolver orders by the same expression) and
			// filterable by date range over that same derived expression — the
			// server resolves the clause through the derived-revisit FROM, never
			// a stored column.
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "planned_revisit_at",
			label: "Revisit (planned)",
			kind: "date",
			prio: 8,
			// The technician's manual revisit date for the place, shown only
			// while it still governs: the server sends null once a Treatment at
			// the Feature is dated at or after it, so the client never derives
			// the supersede rule. A bare calendar day, so the cell renders it as
			// an absolute date (weekday in the tooltip), never a relative
			// phrase. The cell carries the pin icon and the set/clear control,
			// keyed on this column's key in DataTable. The range filter reads
			// only the active plan, so a superseded date is not reachable
			// through it.
			sortable: true,
			filterOps: ["dateRange"],
			width: 170,
		},
		{
			key: "control_state",
			label: "Control",
			kind: "enum",
			prio: 9,
			// The server's derived control verdict (platform.residual
			// ControlState over WindowEnd). Display shows the value the filter
			// narrowed on; the enum filter's vocabulary is the same three
			// values the map bands into expired/controlled/unknown, which is how
			// "Past control" replaces the removed past-control map toggle.
			sortable: false,
			filterOps: ["enum"],
			width: 130,
			options: CONTROL_STATE_OPTIONS,
		},
		{
			key: "active",
			label: "Active",
			kind: "bool",
			prio: 6,
			// The district's standing decision to keep servicing the Feature,
			// inherited from the FieldSeeker source's `active` flag (a deleted
			// source counts as deactivated too). The map hides inactive Features
			// by default; this filter is how the operator includes them
			// deliberately. Same shape as site.has_features: a boolean stored
			// column filtered through the enum true/false control.
			sortable: true,
			filterOps: ["enum"],
			width: 100,
			options: YES_NO_OPTIONS,
		},
	],
	treatment: [
		{
			key: "start_datetime",
			label: "Date",
			kind: "datetime",
			prio: 1,
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "product",
			label: "Product",
			// The cell links to the product's catalog detail page when the
			// district product is catalogued; the enum filter is unchanged.
			kind: "productLink",
			prio: 2,
			sortable: true,
			filterOps: ["enum"],
			width: 180,
			optionsSource: "product",
			optionValueKind: "number",
		},
		{
			key: "intervention",
			label: "Intervention",
			// A physical-control treatment records the district's own
			// intervention instead of a product; the cell is the intervention
			// name, and the enum filter offers this district's interventions.
			// Display-only for sorting (the server's registry keeps it
			// unsortable), like the applied-rate column.
			kind: "text",
			prio: 3,
			sortable: false,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "intervention",
			optionValueKind: "number",
		},
		{
			key: "prescribed_rates",
			label: "Prescribed rate",
			kind: "rates",
			prio: 4,
			// The label's own application scenarios, in the district's preferred
			// units. Display-only: the value is a list, and each rate keeps its
			// own basis (per acre vs per gallon of water vs count).
			sortable: false,
			filterOps: [],
			width: 240,
		},
		{
			key: "applied_rate",
			label: "Applied rate",
			kind: "appliedRate",
			prio: 5,
			// The treatment's own rate and its verdict against the label. The
			// client never computes a rate or a verdict: the server derives both,
			// and this cell only renders them and the gap. Filtering by verdict
			// is the one control: the server resolves the clause to exactly the
			// treatments carrying those verdicts, so the table, its total and the
			// map all agree.
			sortable: false,
			filterOps: ["enum"],
			width: 230,
			options: RATE_VERDICT_OPTIONS,
		},
		{
			key: "quantity",
			label: "Quantity",
			kind: "number",
			prio: 6,
			sortable: true,
			filterOps: ["numberRange"],
			// Observed 0 .. 21,575, half of all rows at 1. A magnitude track over
			// the whole span keeps both 1 and 1,000 draggable.
			rangeDomain: { min: 0.1, max: 100000, log: true },
			width: 100,
			align: "right",
		},
		{
			key: "quantity_unit",
			label: "Unit",
			kind: "text",
			prio: 7,
			// Quantity and Unit sit beside Product: together they describe what
			// was applied and how much.
			sortable: false,
			filterOps: ["contains"],
			width: 90,
		},
		{
			key: "area_treated",
			label: "Area",
			kind: "area",
			prio: 8,
			// Observed 0 .. 30.7M m², median 1.9 m²: nine decades, all of them
			// reachable on a magnitude track.
			rangeDomain: { min: 0.1, max: 100000000, log: true },
			// Sortable and filterable over the stored canonical square-metre
			// value; the cell renders the district's own area unit beside it.
			sortable: true,
			filterOps: ["numberRange"],
			width: 110,
			align: "right",
		},
		{
			key: "vectorsurv_application_id",
			label: "VectorSurv",
			// The VectorSurv application id the treatment was filed under, null
			// until it is sent. Display-only and unsortable/unfilterable: it is
			// an identity readout, shown as plain text (there is no confirmed
			// per-application URL on the VectorSurv Gateway to link to).
			kind: "text",
			prio: 10,
			sortable: false,
			filterOps: [],
			width: 110,
		},
		{
			key: "creator",
			label: "Applicator",
			// The enum offers the organisation's members by id, and the clause
			// filters on the treatment's creator_id — never the display name,
			// which is neither unique nor stable. The cell still shows the
			// joined name.
			kind: "enum",
			prio: 9,
			sortable: true,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "user",
			optionValueKind: "number",
		},
		{
			key: "method",
			label: "Method",
			kind: "enum",
			prio: 11,
			sortable: true,
			filterOps: ["enum"],
			width: 110,
			options: TREATMENT_METHOD_OPTIONS,
		},
		{
			key: "feature",
			label: "Feature",
			kind: "text",
			prio: 12,
			// The related Feature's habitat type — singular, so it shows the
			// related record's label and links to that Feature.
			sortable: false,
			filterOps: [],
			width: 140,
			relation: { target: "feature" },
		},
		{
			key: "site",
			label: "Site",
			kind: "text",
			prio: 13,
			sortable: true,
			filterOps: [],
			width: 240,
			relation: { target: "site" },
		},
	],
	// The Observations ledger: the record of what a technician saw — when, by
	// whom, at which Feature and Site, and what the production check made
	// during it found.
	observation: [
		{
			key: "observed_at",
			label: "Observed",
			kind: "datetime",
			prio: 1,
			// The row's identifier and only sort, exactly as a Treatment is
			// identified by its date: an observation is a moment, and its own
			// id is not something an operator reads.
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "inspection",
			label: "Inspection",
			kind: "enum",
			prio: 2,
			// Derived server-side from the observation's live Inspections. The
			// three readings are the point of the column: production found,
			// found nothing, and no check at all are different facts about
			// different visits and are never merged. The enum filter narrows to
			// exactly the observations whose cell shows a chosen reading.
			sortable: false,
			filterOps: ["enum"],
			width: 160,
			options: Object.entries(INSPECTION_READING_LABELS).map(
				([value, label]) => ({
					value,
					label,
				}),
			),
			valueLabels: INSPECTION_READING_LABELS,
		},
		{
			key: "feature",
			label: "Feature",
			kind: "text",
			prio: 3,
			// The Feature the observation was recorded at, shown by its habitat
			// type and linked to that Feature — following the link narrows the
			// Feature table to it, exactly as the Treatment grid's Feature
			// column does. Not filterable here: the link IS the filter.
			sortable: false,
			filterOps: [],
			width: 150,
			relation: { target: "feature" },
		},
		{
			key: "site",
			label: "Site",
			kind: "text",
			prio: 4,
			sortable: true,
			filterOps: [],
			width: 240,
			relation: { target: "site" },
		},
		{
			key: "creator",
			label: "Technician",
			kind: "enum",
			prio: 5,
			// The organisation's members by id, exactly as on the Treatment
			// grid: the clause filters observation.creator_id, never the joined
			// display name the cell shows. The label is Technician, not
			// Applicator — the ontology separates the two: a Technician records
			// the observation, an Applicator applies the treatment.
			sortable: true,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "user",
			optionValueKind: "number",
			// Where the source named no person, the zone it routed the visit by
			// stands in — the district's own label for who does that ground.
			valueFallback: { key: "zone", prefix: "Zone " },
		},
		{
			key: "density",
			label: "Density",
			kind: "enum",
			prio: 6,
			// An attribute of the Inspection the reading came from, so it is
			// empty where there was no check. Display-only: the ledger's
			// inspection filter is the production reading above.
			sortable: false,
			filterOps: [],
			width: 110,
			valueLabels: INSPECTION_DENSITY_LABELS,
		},
		{
			key: "stage",
			label: "Stage",
			kind: "enum",
			prio: 7,
			sortable: false,
			filterOps: [],
			width: 100,
			valueLabels: INSPECTION_STAGE_LABELS,
		},
		{
			key: "production_state",
			label: "Production state",
			kind: "enum",
			prio: 8,
			// AS OF this observation: the feature_production_state row with the
			// greatest observed_at (ties by greatest id) not later than the
			// observation, resolved server-side from the versioned table — never
			// the _current view. An empty cell means no state was asserted at or
			// before the observation, which is not an asserted `unknown`.
			sortable: false,
			filterOps: ["enum"],
			width: 160,
			options: PRODUCTION_STATE_OPTIONS,
			valueLabels: optionLabels(PRODUCTION_STATE_OPTIONS),
		},
		{
			key: "water_origin",
			label: "Water origin",
			kind: "enum",
			prio: 9,
			// As-of the observation, from feature_classification's water_origin
			// dimension. Filterable; the cell shows the same as-of value the
			// clause tests.
			sortable: false,
			filterOps: ["enum"],
			width: 190,
			options: WATER_ORIGIN_OPTIONS,
			valueLabels: optionLabels(WATER_ORIGIN_OPTIONS),
		},
		{
			key: "flow_state",
			label: "Flow",
			kind: "enum",
			prio: 10,
			// As-of the observation, from feature_classification's flow_state
			// dimension.
			sortable: false,
			filterOps: ["enum"],
			width: 130,
			options: FLOW_STATE_OPTIONS,
			valueLabels: optionLabels(FLOW_STATE_OPTIONS),
		},
		{
			key: "organic_content",
			label: "Organic content",
			kind: "enum",
			prio: 11,
			// As-of the observation, from feature_classification's
			// organic_content dimension.
			sortable: false,
			filterOps: ["enum"],
			width: 140,
			options: ORGANIC_CONTENT_OPTIONS,
			valueLabels: optionLabels(ORGANIC_CONTENT_OPTIONS),
		},
		{
			key: "vegetation_influence",
			label: "Vegetation influence",
			kind: "enum",
			prio: 12,
			// As-of the observation, from feature_classification's
			// vegetation_influence dimension. The clone has ZERO rows of that
			// dimension today: the column and its filter ship ahead of capture,
			// and every row renders the honest empty cell rather than a
			// fabricated value.
			sortable: false,
			filterOps: ["enum"],
			width: 170,
			options: VEGETATION_INFLUENCE_OPTIONS,
			valueLabels: optionLabels(VEGETATION_INFLUENCE_OPTIONS),
		},
		{
			key: "volume",
			label: "Volume",
			kind: "number",
			prio: 13,
			// As-of the observation, from feature_classification's `size`
			// dimension. Null where that measure was not recorded; length is not
			// exposed. Range-filterable over the same expression the cell shows.
			sortable: false,
			filterOps: ["numberRange"],
			rangeDomain: { min: 0.1, max: 100000000, log: true },
			width: 110,
			align: "right",
		},
		{
			key: "area",
			label: "Area",
			kind: "number",
			prio: 14,
			// As-of the observation, from feature_classification's `size`
			// dimension. Null where that measure was not recorded.
			sortable: false,
			filterOps: ["numberRange"],
			rangeDomain: { min: 0.1, max: 100000000, log: true },
			width: 110,
			align: "right",
		},
	],
	// The Traps surface: one row per Trap Station (§23). Deliberately thin —
	// the station carries no trap type, lure or datetimes — and the only filter
	// is Active. Its Site is a relation link and its Instances is a display-only
	// correlated count whose cell links to the counts surface narrowed to the
	// station.
	trap_station: [
		{
			key: "id",
			label: "Location",
			kind: "number",
			prio: 1,
			// The trap location's own identifier, shown because a Trap Location
			// has no label; it is not filterable, like every identifier in the
			// ledger.
			sortable: true,
			filterOps: [],
			width: 110,
			align: "right",
		},
		{
			key: "site",
			label: "Site",
			kind: "text",
			prio: 2,
			// Display-only: the trap family has no edge to the site survey
			// ledgers, so this cell shows the Site's label and never narrows it.
			sortable: true,
			filterOps: [],
			width: 220,
		},
		{
			key: "active",
			label: "Active",
			kind: "bool",
			prio: 3,
			// The district's standing decision to keep trapping here; a station
			// is retired rather than deleted.
			sortable: true,
			filterOps: ["enum"],
			width: 100,
			options: [
				{ value: "true", label: "Active" },
				{ value: "false", label: "Retired" },
			],
		},
		{
			key: "instances",
			label: "Instances",
			kind: "number",
			prio: 4,
			// The station's trapping series: a display-only correlated count
			// whose cell links to the counts surface, exactly as the Site grid's
			// Features column does.
			sortable: false,
			filterOps: [],
			width: 110,
			align: "right",
			relation: { target: "trap_instance" },
		},
	],
	// The Trap Instances surface: one row per Trap Instance (§24), pivoting its
	// sample's counts, pools and tests for reading.
	trap_instance: [
		{
			key: "date",
			label: "Date",
			kind: "datetime",
			prio: 1,
			// The instance's set datetime (its identifier and default sort). The
			// cell shows it with the collect datetime and the interval inline, so
			// one column carries what three used to.
			sortable: true,
			filterOps: ["dateRange"],
			width: 190,
		},
		{
			key: "station",
			label: "Station",
			kind: "text",
			prio: 2,
			// The relation link to the associated station; empty for an
			// off-station instance, which is normal.
			sortable: false,
			filterOps: [],
			width: 110,
			relation: { target: "trap_station" },
		},
		{
			// Display-only: the trap family has no edge to the site survey
			// ledgers, so this cell shows the Site's label and never narrows it.
			key: "site",
			label: "Site",
			kind: "text",
			prio: 3,
			sortable: true,
			filterOps: [],
			width: 200,
		},
		{
			key: "trap_type",
			label: "Trap type",
			kind: "enum",
			prio: 4,
			sortable: true,
			filterOps: ["enum"],
			width: 130,
			optionsSource: "trapType",
			optionValueKind: "number",
		},
		{
			key: "lure",
			label: "Lure",
			kind: "enum",
			prio: 5,
			sortable: true,
			filterOps: ["enum"],
			width: 120,
			optionsSource: "lure",
			optionValueKind: "number",
		},
		{
			key: "counts",
			label: "Counts",
			kind: "counts",
			prio: 6,
			// The catch's plain total and female total. Display-only: the catch's
			// own dimensions are the Mosquito Counts table's columns, and the
			// full species × sex matrix is in the row's rail detail.
			sortable: false,
			filterOps: [],
			width: 130,
		},
		{
			key: "positivity",
			label: "Positivity",
			kind: "enum",
			prio: 7,
			sortable: false,
			filterOps: ["enum"],
			width: 110,
			options: POSITIVITY_OPTIONS,
			valueLabels: POSITIVITY_LABELS,
		},
		{
			key: "pools",
			label: "Pools",
			kind: "number",
			prio: 8,
			// The sample's pool count, display-only.
			sortable: false,
			filterOps: [],
			width: 80,
			align: "right",
		},
		{
			key: "state",
			label: "State",
			kind: "enum",
			prio: 9,
			// The instance's state, derived from the collect datetime.
			sortable: false,
			filterOps: ["enum"],
			width: 100,
			options: INSTANCE_STATE_OPTIONS,
			valueLabels: INSTANCE_STATE_LABELS,
		},
	],
	// The Mosquito Counts surface: one row per Species Count (§26), normalised —
	// a species, a sex and a count. Its Trap Instance, Station and Site are
	// reached through the sample.
	species_count: [
		{
			key: "set_at",
			label: "Set",
			kind: "datetime",
			prio: 1,
			// The instance's set date, reached through the sample: the row's
			// ordering and its date filter.
			sortable: true,
			filterOps: ["dateRange"],
			width: 160,
		},
		{
			key: "station",
			label: "Station",
			kind: "text",
			prio: 2,
			sortable: false,
			filterOps: [],
			width: 110,
			relation: { target: "trap_station" },
		},
		{
			// Display-only: the trap family has no edge to the site survey
			// ledgers, so this cell shows the Site's label and never narrows it.
			key: "site",
			label: "Site",
			kind: "text",
			prio: 3,
			sortable: true,
			filterOps: [],
			width: 200,
		},
		{
			key: "species",
			label: "Species",
			kind: "enum",
			prio: 4,
			sortable: true,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "species",
			optionValueKind: "number",
		},
		{
			key: "sex",
			label: "Sex",
			kind: "enum",
			prio: 5,
			// The vocabulary's three facets: the members are the dimension's own
			// values, so they are static here, and the cell shows the vocabulary
			// row's own name beside them.
			sortable: true,
			filterOps: ["enum"],
			width: 130,
			options: SEX_FACET_OPTIONS,
		},
		{
			key: "count",
			label: "Count",
			kind: "number",
			prio: 6,
			sortable: true,
			filterOps: ["numberRange"],
			width: 100,
			align: "right",
			rangeDomain: { min: 1, max: 100000, log: true },
		},
	],
	// The Trap Samples surface: one row per collected Trap Sample (§25) — the
	// worklist the lab counts from, and the reconciliation list beside it. The
	// sample's own word is the LABEL the district wrote on the container, which
	// is the string this bench reads and searches. The station, the site, the
	// two datetimes and the trap type are the INSTANCE's, read through the
	// instance it came from.
	trap_sample: [
		{
			key: "label",
			label: "Label",
			kind: "text",
			prio: 1,
			// The district's own handwriting on the physical container — the
			// row's sticky first cell, and the thing the eye tells rows apart
			// by. The workbench records it and never mints it, so it stays a
			// contains-match over what the district wrote.
			sortable: true,
			filterOps: ["contains"],
			width: 150,
		},
		{
			key: "collected_at",
			label: "Collected",
			kind: "datetime",
			prio: 2,
			// The instance's collect datetime, which is what makes a sample a
			// collected one: the label's neighbouring column and the list's
			// default order, newest first.
			sortable: true,
			filterOps: ["dateRange"],
			width: 160,
		},
		{
			key: "set_at",
			label: "Set",
			kind: "datetime",
			prio: 3,
			// The instance's set date, reached through the sample: the other end
			// of the trapping effort the row is traceable to.
			sortable: true,
			filterOps: ["dateRange"],
			width: 160,
		},
		{
			key: "station",
			label: "Station",
			kind: "text",
			prio: 4,
			// The relation link to the station the instance was set at; empty
			// for an off-station instance, which is normal.
			sortable: false,
			filterOps: [],
			width: 110,
			relation: { target: "trap_station" },
		},
		{
			// Display-only: the trap family has no edge to the site survey
			// ledgers, so this cell shows the Site's label and never narrows it.
			key: "site",
			label: "Site",
			kind: "text",
			prio: 5,
			sortable: true,
			filterOps: [],
			width: 200,
		},
		{
			key: "trap_type",
			label: "Trap type",
			kind: "enum",
			prio: 6,
			// The set's own parameter, read through the instance: the member set
			// is the district's live vocabulary, resolved from the store rather
			// than declared here.
			sortable: true,
			filterOps: ["enum"],
			width: 130,
			optionsSource: "trapType",
			optionValueKind: "number",
		},
		{
			key: "state",
			label: "State",
			kind: "enum",
			prio: 7,
			// The stored analysis state: the worklist is this column filtered to
			// "Not analyzed".
			sortable: true,
			filterOps: ["enum"],
			width: 120,
			options: SAMPLE_STATE_OPTIONS,
			valueLabels: SAMPLE_STATE_LABELS,
		},
		{
			key: "counts",
			label: "Counts",
			kind: "number",
			prio: 8,
			// The number of rows in the sample's live composition: how many
			// species × sex counts the current session holds. Display-only, with
			// the composition itself in the sample's own detail.
			sortable: false,
			filterOps: [],
			width: 90,
			align: "right",
		},
		{
			key: "holding",
			label: "Holding",
			kind: "bool",
			prio: 9,
			// Whether any of the sample was set aside into a holding container:
			// the `?` marker the operator reconciles the box against, never a
			// count.
			sortable: false,
			filterOps: [],
			width: 90,
		},
	],
	// The Zones surface: one row per Zone read from its active version — the
	// district's own named units (doc/design/mvp/zones.md), which are not
	// ontology objects but the district's own overlay on the record. The
	// surface is deliberately NOT narrowed by its own zone list: the grid lists
	// the units so they can be narrowed by.
	zone: [
		{
			key: "name",
			label: "Name",
			kind: "text",
			prio: 1,
			// The active version's name: the surface's identifier and the reason
			// its default sort is name-ascending, not newest-first.
			sortable: true,
			filterOps: ["contains"],
			width: 200,
		},
		{
			key: "comment",
			label: "Comment",
			kind: "text",
			prio: 2,
			// The active version's comment, free text in the district's own
			// words. Filterable, but not sortable: ordering by a comment orders
			// by nothing.
			sortable: false,
			filterOps: ["contains"],
			width: 260,
		},
		{
			key: "records",
			label: "Records",
			kind: "number",
			prio: 3,
			// Display-only, exactly as the Site grid's Features column is: the
			// value is a correlated count of the Features the zone's active
			// version contains, so ordering by it renders invalid SQL and
			// filtering by it scans every candidate zone. The cell links to the
			// Feature ledger narrowed to this zone.
			sortable: false,
			filterOps: [],
			width: 110,
			align: "right",
			zoneLink: { target: "feature" },
		},
		{
			key: "created",
			label: "Created",
			kind: "datetime",
			prio: 4,
			sortable: true,
			filterOps: ["dateRange"],
			width: 150,
		},
		{
			key: "active",
			label: "Active",
			kind: "bool",
			prio: 5,
			// The unit's life: a zone is retired rather than deleted, and a
			// retired zone drops from the default view and from every zone
			// narrowing while its versions stand.
			sortable: true,
			filterOps: ["enum"],
			width: 100,
			options: [
				{ value: "true", label: "Active" },
				{ value: "false", label: "Retired" },
			],
		},
	],
	// The Pools surface: one row per Pool (§26) × recorded test — the lab's
	// pooled composition, read beside the Counts ledger on the lab records page.
	// The pool's own facts are its species, its sex and its size; the test's are
	// the target, the method, the status and the test date it came back with, and
	// they are empty on a pool that carries no test. Its set date, station, site
	// and the sample it was counted in are reached through the sample, so those
	// are relation links rather than columns of its own — and the Counts cell is
	// the cross-ledger link, which reads this pool's sample's counts in the
	// Counts ledger on the same page.
	pool: [
		{
			key: "id",
			label: "Pool",
			kind: "number",
			prio: 1,
			// The pool's own identifier, shown because the district writes a
			// pool's label after recording it — the label is nullable, so it
			// cannot be the cell the eye tells rows apart by. Not filterable,
			// like every identifier in the ledger.
			sortable: true,
			filterOps: [],
			width: 100,
			align: "right",
		},
		{
			key: "label",
			label: "Label",
			kind: "text",
			prio: 2,
			// The district's handwriting on the pool, which is what the bench and
			// the pool sheet read it by; empty until somebody writes one.
			sortable: true,
			filterOps: ["contains"],
			width: 130,
		},
		{
			key: "species",
			label: "Species",
			kind: "enum",
			prio: 3,
			sortable: true,
			filterOps: ["enum"],
			width: 160,
			optionsSource: "species",
			optionValueKind: "number",
		},
		{
			key: "sex",
			label: "Sex",
			kind: "enum",
			prio: 4,
			// The sex vocabulary, by facet: the pool's sex is one of the three
			// facets, so a filter reaches every state that facet covers rather
			// than one spelled name of it.
			sortable: true,
			filterOps: ["enum"],
			width: 110,
			optionsSource: "sex",
		},
		{
			key: "size",
			label: "Size",
			kind: "number",
			prio: 5,
			// How many mosquitoes were pooled, which is what makes the test's
			// result mean anything. Display-only: the pools are counted, not
			// searched by count.
			sortable: true,
			filterOps: [],
			width: 90,
			align: "right",
		},
		{
			key: "positivity",
			label: "Positivity",
			kind: "enum",
			prio: 6,
			// The pool's own three-valued reading of its tests (§28 System Rule
			// 1), the same vocabulary the instance's Positivity column reads.
			sortable: false,
			filterOps: ["enum"],
			width: 110,
			options: POSITIVITY_OPTIONS,
			valueLabels: POSITIVITY_LABELS,
		},
		{
			key: "target",
			label: "Target",
			kind: "enum",
			prio: 7,
			// The target the test looked for: the district's own test_target
			// vocabulary, by id, resolved from the store rather than declared
			// here.
			sortable: false,
			filterOps: ["enum"],
			width: 120,
			optionsSource: "testTarget",
			optionValueKind: "number",
		},
		{
			key: "method",
			label: "Method",
			kind: "enum",
			prio: 8,
			// The method the laboratory tested by, from the district's
			// test_method vocabulary, by id.
			sortable: false,
			filterOps: ["enum"],
			width: 130,
			optionsSource: "testMethod",
			optionValueKind: "number",
		},
		{
			key: "status",
			label: "Status",
			kind: "enum",
			prio: 9,
			// What that one test came back as — the four result states, which are
			// the test_status vocabulary's facets and are static here as the
			// instance's positivity is. Empty on a pool that carries no test.
			sortable: false,
			filterOps: ["enum"],
			width: 120,
			options: TEST_STATUS_OPTIONS,
			valueLabels: TEST_STATUS_LABELS,
		},
		{
			key: "test_date",
			label: "Tested",
			kind: "datetime",
			prio: 10,
			// The day the laboratory ran this test; empty while the pool is
			// untested.
			sortable: true,
			filterOps: ["dateRange"],
			width: 160,
		},
		{
			key: "results_complete_at",
			label: "Results complete",
			kind: "datetime",
			prio: 11,
			// When every result the pool is waiting on had arrived. Empty while
			// one is still outstanding, which is how the lab reads what is done.
			sortable: true,
			filterOps: ["dateRange"],
			width: 170,
		},
		{
			key: "set_at",
			label: "Set",
			kind: "datetime",
			prio: 12,
			// The instance's set date, reached through the sample: the row's own
			// ordering and the date its map dot sits on.
			sortable: true,
			filterOps: ["dateRange"],
			width: 160,
		},
		{
			key: "station",
			label: "Station",
			kind: "text",
			prio: 13,
			// The relation link to the station the instance was set at; empty for
			// an off-station instance, which is normal.
			sortable: false,
			filterOps: [],
			width: 110,
			relation: { target: "trap_station" },
		},
		{
			// Display-only: the trap family has no edge to the site survey
			// ledgers, so this cell shows the Site's label and never narrows it.
			key: "site",
			label: "Site",
			kind: "text",
			prio: 14,
			sortable: true,
			filterOps: [],
			width: 200,
		},
		{
			key: "sample_label",
			label: "Sample",
			kind: "text",
			prio: 15,
			// The relation link to the sample this pool was counted in. The key is
			// the row's own `sample_label`, not the resource's filter property
			// (`sample`, the same fact read from the sample side): the cell reads
			// the row, and the link needs no clause of its own — it narrows the
			// sample ledger by this pool's id.
			sortable: false,
			filterOps: [],
			width: 130,
			relation: { target: "trap_sample" },
		},
		{
			key: "counts",
			label: "Counts",
			kind: "number",
			prio: 16,
			// The cross-lab link: this pool's sample's counts, read in the Counts
			// ledger, which is the surface this page shows beside the pools. The
			// pool payload carries no count of its own, so the cell has no value
			// to state — the link is the whole of it.
			sortable: false,
			filterOps: [],
			width: 100,
			align: "right",
			relation: { target: "species_count" },
		},
	],
};

/** The table's default sort, i.e. what `sort: null` means on the wire. */
export function defaultSort(table: MapReviewTable): MapReviewSort {
	switch (table) {
		case "site":
			return { column: "created", desc: true };
		case "feature":
			return { column: "created", desc: true };
		case "treatment":
			return { column: "start_datetime", desc: true };
		case "observation":
			// Newest first, like the other three: the record's own moment is its
			// identifier, and the server orders by the same expression.
			return { column: "observed_at", desc: true };
		case "trap_station":
			// A trap location has no label to order by; its identity is its
			// identifier, newest first.
			return { column: "id", desc: true };
		case "trap_instance":
			// The row's identifier is the set datetime: newest set first.
			return { column: "date", desc: true };
		case "species_count":
			// The instance's set date, reached through the sample: newest set
			// first.
			return { column: "set_at", desc: true };
		case "trap_sample":
			// The reconciliation list reads the box in front of the operator:
			// the most recently collected sample is at the top, exactly as the
			// trap instances' own default is the newest set.
			return { column: "collected_at", desc: true };
		case "zone":
			// A zone's identity is its name, and the ledger wants to read the
			// district's units as a list, so it sorts by name rather than by
			// creation.
			return { column: "name", desc: false };
		case "pool":
			// The instance's set date, reached through the sample, exactly as the
			// Mosquito counts ledger reads a row's freshness: the pool itself
			// carries no datetime of its own, and a pool's test date is empty
			// until it is tested.
			return { column: "set_at", desc: true };
	}
}

export function columnByKey(
	table: MapReviewTable,
	key: string,
): MapReviewColumn | undefined {
	return MAP_REVIEW_COLUMNS[table].find((c) => c.key === key);
}

/**
 * The labeled reading of one column's aggregate, as an ordered list of lines:
 * the aggregate over the whole filtered set, in the operator's words. The
 * header tooltip joins the lines, so several values never truncate each other.
 * An empty list means the column carries no summary at all, and its label gets
 * no tooltip.
 *
 * Numbers read exactly as the cells' numbers do (the same `toLocaleString`),
 * so a column's total and the values it totals are in one arithmetic. A day
 * range reads through formatDayLabel, the fixed day/month vocabulary the filter
 * trigger already names a date in: `new Date(...)` would re-read a bare day as
 * UTC midnight and show the previous day west of Greenwich, and a locale call
 * would read differently in two windows and differently again in a test.
 */
export function summaryLines(
	column: MapReviewColumn,
	summary: MapReviewColumnSummary | undefined,
): string[] {
	if (!summary) return [];
	switch (summary.kind) {
		case "sum":
		case "sum_avg": {
			if (summary.sum === null) return [];
			const lines = [`Sum: ${summary.sum.toLocaleString()}`];
			// The average is only meaningful beside the sum it divides, and the
			// server sends one only for the sum_avg columns.
			if (summary.avg !== null)
				lines.push(`Avg: ${summary.avg.toLocaleString()}`);
			return lines;
		}
		case "range": {
			// Both ends or nothing: one end of a range is not a range, and the
			// server nulls both when the keep set carries no value at all.
			if (summary.min === null || summary.max === null) return [];
			// The server sends each end already formatted as the column's own
			// kind formats it — a bare "YYYY-MM-DD" for a date column, an
			// RFC3339 instant for a datetime one, whose first ten characters
			// are the calendar day it names.
			const ends = [summary.min, summary.max].map((value) =>
				formatDayLabel(column.kind === "date" ? value : value.slice(0, 10)),
			);
			return [`Range: ${ends[0]} – ${ends[1]}`];
		}
		case "distinct":
			return summary.distinct === null
				? []
				: [`Distinct: ${summary.distinct.toLocaleString()}`];
		case "positivity":
			// One line per test target, so a district testing for several
			// pathogens reads each beside its own count.
			return summary.targets.map(
				(target) => `${target.target}: ${target.count.toLocaleString()}`,
			);
		default:
			return [];
	}
}

/** Columns of a table carrying a control in the embedded filter row. */
export function filterableColumns(table: MapReviewTable): MapReviewColumn[] {
	return MAP_REVIEW_COLUMNS[table].filter((c) => c.filterOps.length > 0);
}

/**
 * toggleEnumSelection adds (or removes) one option from an enum column's
 * current selection. The result is ordered by the option vocabulary, exactly as
 * the native multi-select reported its selectedOptions, so the emitted clause
 * keeps the vocabulary's order regardless of the order the operator clicked in.
 * A selected value the vocabulary does not list — a product whose store has not
 * loaded, say — stays selected after the known ones rather than being dropped.
 * An empty result is the caller's cleared clause (null on the wire).
 */
export function toggleEnumSelection(
	options: readonly MapReviewColumnOption[],
	selected: readonly (string | number)[],
	toggled: string | number,
): (string | number)[] {
	const wanted = new Set(selected.map(String));
	const key = String(toggled);
	if (wanted.has(key)) wanted.delete(key);
	else wanted.add(key);
	const result: (string | number)[] = [];
	const taken = new Set<string>();
	for (const option of options) {
		const optionKey = String(option.value);
		if (wanted.has(optionKey) && !taken.has(optionKey)) {
			result.push(option.value);
			taken.add(optionKey);
		}
	}
	for (const value of selected) {
		const valueKey = String(value);
		if (wanted.has(valueKey) && !taken.has(valueKey)) {
			result.push(value);
			taken.add(valueKey);
		}
	}
	return result;
}

/**
 * selectAllEnumValues is the panel's "All" bulk action: the whole vocabulary the
 * column offers, in the column's own order, deduplicated. Because the option
 * list is what the caller resolved at render time (the store-backed product and
 * intervention lists, or the ~49 Feature types), All ticks exactly the options
 * on screen — and the clause it emits is the same one ticking every checkbox
 * would. An empty vocabulary selects nothing, which the caller maps to a
 * cleared clause rather than an empty selection.
 */
export function selectAllEnumValues(
	options: readonly MapReviewColumnOption[],
): (string | number)[] {
	const values: (string | number)[] = [];
	const seen = new Set<string>();
	for (const option of options) {
		const key = String(option.value);
		if (seen.has(key)) continue;
		seen.add(key);
		values.push(option.value);
	}
	return values;
}

/**
 * enumSelectionLabels names the selected values in the operator's words, in the
 * option vocabulary's order. A value with no matching option falls back to its
 * raw string so a clause never goes unlabelled.
 */
export function enumSelectionLabels(
	options: readonly MapReviewColumnOption[],
	values: readonly (string | number)[] | null | undefined,
): string[] {
	return (values ?? []).map((value) => {
		const option = options.find((o) => String(o.value) === String(value));
		return option?.label ?? String(value);
	});
}

/**
 * enumSelectionSummary is what the filter trigger shows without the panel open:
 * the label itself when one option is chosen, both labels while they still fit
 * the narrow header cell, and a count once the selection is longer than that.
 * Empty when nothing is selected (the trigger's "All").
 */
export function enumSelectionSummary(
	options: readonly MapReviewColumnOption[],
	values: readonly (string | number)[] | null | undefined,
): string {
	const labels = enumSelectionLabels(options, values);
	if (labels.length === 0) return "";
	if (labels.length === 1) return labels[0];
	const joined = labels.join(", ");
	return labels.length === 2 && joined.length <= 20
		? joined
		: `${labels.length} selected`;
}

/** Human label for an active relation filter, e.g. "Sites related to Treatment #42". */
export function describeRelationFilter(f: MapReviewColumnFilter): string {
	const source = f.source ? MAP_REVIEW_TABLE_SINGULAR[f.source] : "record";
	return `${MAP_REVIEW_TABLE_LABELS[f.table]} related to ${source} #${f.source_id ?? "?"}`;
}

/** One column filter's value phrase, in the operator's words. Exported so the
 * toolbar can name a clause set on ANOTHER table, which narrows this one's
 * counts and cannot appear in this table's filter row. */
export function describeClauseValue(
	f: MapReviewColumnFilter,
	column: MapReviewColumn | undefined,
	options?: MapReviewColumnOption[],
): string {
	switch (f.op) {
		case "contains":
			return `${column?.label ?? f.column} contains "${f.text ?? ""}"`;
		case "numberRange": {
			const bounds: string[] = [];
			if (f.min != null) bounds.push(`min ${f.min}`);
			if (f.max != null) bounds.push(`max ${f.max}`);
			return bounds.length > 0
				? `${column?.label ?? f.column} ${bounds.join(", ")}`
				: `${column?.label ?? f.column} (any)`;
		}
		case "dateRange": {
			const bounds: string[] = [];
			if (f.from) bounds.push(`from ${formatDayLabel(f.from)}`);
			if (f.to) bounds.push(`to ${formatDayLabel(f.to)}`);
			return bounds.length > 0
				? `${column?.label ?? f.column} ${bounds.join(" ")}`
				: `${column?.label ?? f.column} (any)`;
		}
		case "enum": {
			// The per-district id columns — product, intervention, applicator,
			// technician — carry no static options: their members are the
			// district's own, so the caller resolves them. Without that, this
			// reads "Product is 5" instead of the product's name. An empty
			// resolution falls back to the column's own options rather than
			// erasing them: most columns carry their labels statically, and only
			// the caller knows which it is looking at.
			const vocabulary =
				options && options.length > 0 ? options : (column?.options ?? []);
			const chosen = (f.values ?? []).map((value) => {
				const option = vocabulary.find(
					(o) => String(o.value) === String(value),
				);
				return option?.label ?? String(value);
			});
			return chosen.length > 0
				? `${column?.label ?? f.column} is ${chosen.join(" or ")}`
				: `${column?.label ?? f.column} (any)`;
		}
		default:
			return `${column?.label ?? f.column} ${f.op}`;
	}
}

/**
 * describeSpec renders a whole filter spec in plain words, one clause per line:
 * every column filter of every table, the search boxes, the drawn area, the
 * selected zones and the session's temporary zones (each named by the label the
 * buffer was materialised under). It is how the bulk planned-revisit confirm
 * reads the composed filter back — a clause like "Treatment Product is Fish"
 * narrows Features through the resolver, and the zone narrowing is a geographic
 * clause in the same class as the drawn area, so the echo must cover every
 * table, not just the one on screen. Returns an empty array when the spec
 * filters nothing.
 *
 * `optionsFor` resolves a column's enum labels when they are not static — the
 * per-district product, intervention and person columns, whose members are the
 * district's own ids. Without it those lines read "Product is 5", naming the id
 * the clause is stored as rather than what the operator picked.
 */
export function describeSpec(
	spec: MapReviewSpec,
	optionsFor?: (
		table: MapReviewTable,
		column: string,
	) => MapReviewColumnOption[],
): string[] {
	const lines: string[] = [];
	for (const t of MAP_REVIEW_TABLES) {
		const text = spec.search[t]?.trim();
		if (text) lines.push(`${MAP_REVIEW_TABLE_LABELS[t]} matching "${text}"`);
	}
	for (const f of spec.filters) {
		if (f.op === "relation") {
			lines.push(describeRelationFilter(f));
			continue;
		}
		const column = columnByKey(f.table, f.column);
		lines.push(
			`${MAP_REVIEW_TABLE_SINGULAR[f.table]} ${describeClauseValue(f, column, optionsFor?.(f.table, f.column))}`,
		);
	}
	// The zone narrowing is one clause covering the union of the selected zones,
	// so it reads as one line whatever the count, naming the count only when
	// there is more than one zone.
	if (spec.zones.length === 1) lines.push("inside the selected zone");
	else if (spec.zones.length > 1) {
		lines.push(`inside the selected zones (${spec.zones.length})`);
	}
	// Each temporary zone is its own clause, unlike the standing zones' single
	// line: a buffer is a shape the operator materialised on the spot, and the
	// server's label for it names the distance and the subject it was built
	// from, so the confirm says which buffer narrowed the write rather than
	// only how many did.
	for (const zone of spec.temp_zones) {
		lines.push(
			`${zone.inside === false ? "outside" : "inside"} the buffer "${zone.name}"`,
		);
	}
	return lines;
}

/**
 * revisitDateToInstant turns the planned-revisit date input's bare calendar day
 * into the RFC3339 instant the API stores. The pin is a *day*, not a moment, so
 * the day is anchored at local noon: the instant then falls on the calendar day
 * the operator typed for them, while still comparing cleanly (at-or-after)
 * against a Treatment's start_datetime.
 */
export function revisitDateToInstant(day: string): string {
	return new Date(`${day}T12:00:00`).toISOString();
}

/**
 * clientZoneOffsetMinutes is the viewer's zone offset in minutes east of UTC —
 * -420 for a California district in PDT. It travels with a date range so the
 * server reads the picked day as the viewer's day; without it "today" means a
 * UTC day, which ends mid-afternoon locally and drops most of the day's work.
 */
export function clientZoneOffsetMinutes(): number {
	return -new Date().getTimezoneOffset();
}

// The months as the UI spells them. A fixed table rather than a locale call:
// every other label in this interface is English, and a date that changed with
// the runtime's locale would read differently in two places in the same window
// and differently again in a test.
const MONTHS_SHORT = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/**
 * The wire's "YYYY-MM-DD" in the operator's words — "30 Jul 2026". Built from
 * the parts rather than `new Date(day)`, which parses the string as UTC
 * midnight and so shows the previous day for every viewer west of Greenwich,
 * and from a fixed month table rather than `toLocaleDateString`, so the same
 * day reads the same everywhere — in the filter trigger, in a clause summary and
 * in a test.
 */
export function formatDayLabel(day: string): string {
	const [year, month, date] = day.split("-").map(Number);
	if (!year || !month || !date || month < 1 || month > 12) return day;
	return `${date} ${MONTHS_SHORT[month - 1]} ${year}`;
}

/** A local calendar day as the wire's "YYYY-MM-DD". */
export function localDayString(d: Date): string {
	const month = `${d.getMonth() + 1}`.padStart(2, "0");
	const day = `${d.getDate()}`.padStart(2, "0");
	return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Parses a cell's date value. A bare "YYYY-MM-DD" is the LOCAL calendar day it
 * states — `new Date("2026-08-12")` parses as UTC midnight, which renders as the
 * previous day for every viewer west of Greenwich — and anything else is the
 * instant it states.
 */
export function parseCellDate(value: string): Date {
	return /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T00:00:00`)
		: new Date(value);
}

/**
 * The Sunday of the viewer's current week. The week runs Sunday to Saturday —
 * the district's week — and "This week" is measured from this day to today, so
 * the range never shows a date in the future.
 */
export function startOfLocalWeek(): string {
	const now = new Date();
	const sunday = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate() - now.getDay(),
	);
	return localDayString(sunday);
}

/**
 * fitColumns keeps the widest columns and drops the narrowest-priority ones
 * (highest `prio` number first, never prio 1) until the registry's total width
 * is within `cutOff` of `availableWidth`. Ties are impossible: prios are unique
 * per table.
 *
 * The loop stops while up to `cutOff` pixels still overflow, deliberately: the
 * rightmost column stays visible but cut off, which is the affordance that
 * there are more columns and the table scrolls sideways. Fitting exactly would
 * leave no sign that the registry holds more.
 */
export function fitColumns(
	columns: MapReviewColumn[],
	availableWidth: number,
	cutOff = 48,
): MapReviewColumn[] {
	if (!Number.isFinite(availableWidth) || availableWidth <= 0) return columns;
	const kept = [...columns];
	let total = kept.reduce((sum, c) => sum + c.width, 0);
	while (total - availableWidth > cutOff) {
		let dropIndex = -1;
		for (let i = 0; i < kept.length; i++) {
			if (kept[i].prio <= 1) continue;
			if (dropIndex === -1 || kept[i].prio > kept[dropIndex].prio) {
				dropIndex = i;
			}
		}
		if (dropIndex === -1) break; // only prio 1 remains: never drop it
		total -= kept[dropIndex].width;
		kept.splice(dropIndex, 1);
	}
	return kept;
}

export interface WindowRange {
	/** First rendered row index (inclusive). */
	start: number;
	/** Last rendered row index (exclusive). */
	end: number;
	/** Pixel offset of `start` inside the row list (top spacer height). */
	offset: number;
}

/**
 * computeWindowRange is the fixed-row-height window over `count` rows: only the
 * rows that intersect the viewport (plus `overscan` on either side) are
 * rendered, so a 116k-row dataset costs the same DOM as a screenful. A table
 * with a sticky header keeps its layout by carrying two spacer rows sized from
 * `offset` and `count - end`.
 */
export function computeWindowRange(
	count: number,
	scrollTop: number,
	viewportHeight: number,
	rowHeight: number,
	overscan = 6,
): WindowRange {
	if (count <= 0 || rowHeight <= 0) return { start: 0, end: 0, offset: 0 };
	const top = Math.max(0, scrollTop);
	const first = Math.floor(top / rowHeight);
	const visible = Math.ceil(Math.max(0, viewportHeight) / rowHeight) + 1;
	const start = Math.max(0, first - overscan);
	const end = Math.min(count, first + visible + overscan);
	return { start, end, offset: start * rowHeight };
}
